import React from "react";
import { motion } from "framer-motion";
import { useTheme } from "../contexts/ThemeContext";

/* Theme palette for the phone mockup contents.
   Single source of truth so AdminScreen / MapScreen / floating cards stay
   visually consistent across light and dark themes. */
type Palette = {
  appBg: string;          // app screen background
  surface: string;        // header / nav-bar / inner cards
  surfaceAlt: string;     // tinted alt surface
  divider: string;        // 1px separator
  textStrong: string;     // primary content text (the "#1D3047" tone in light)
  textMuted: string;      // labels / secondary text ("#aaa" tone in light)
  textDim: string;        // smallest fineprint
  shadow: string;         // box-shadow for inner cards
  mapTerrain: string;     // base map tile colour
  mapBlock: string;       // building block on map
  mapBlock2: string;
  mapBlock3: string;
  mapRoad: string;        // road colour
  mapRoadAlt: string;     // sub road
  mapText: string;        // street labels
  mapTextSub: string;
};

const PALETTES: Record<"light" | "dark", Palette> = {
  light: {
    appBg: "#F4F6FB",
    surface: "white",
    surfaceAlt: "#F0F0F0",
    divider: "#f0f0f0",
    textStrong: "#1D3047",
    textMuted: "#aaa",
    textDim: "#bbb",
    shadow: "0 1px 4px rgba(0,0,0,0.06)",
    mapTerrain: "#EEE9DC",
    mapBlock: "#E5DEC9",
    mapBlock2: "#E8E2CC",
    mapBlock3: "#E2DBCA",
    mapRoad: "#D5CAB2",
    mapRoadAlt: "#C8BC9E",
    mapText: "#999",
    mapTextSub: "#888",
  },
  dark: {
    appBg: "#0f1117",
    surface: "#181c26",
    surfaceAlt: "#222a3a",
    divider: "#222a3a",
    textStrong: "#eef0f5",
    textMuted: "#8a93a6",
    textDim: "#6b7488",
    shadow: "0 1px 4px rgba(0,0,0,0.45)",
    mapTerrain: "#13161e",
    mapBlock: "#1b2230",
    mapBlock2: "#181c26",
    mapBlock3: "#1b2230",
    mapRoad: "#2a3346",
    mapRoadAlt: "#3a4358",
    mapText: "#5a6478",
    mapTextSub: "#6b7488",
  },
};

/* ─── Admin screen ─── */
function AdminScreen({ p }: { p: Palette }) {
  return (
    <div style={{ width:"100%", height:"100%", background:p.appBg, display:"flex", flexDirection:"column", fontFamily:"Inter,system-ui,sans-serif" }}>
      {/* status bar */}
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"6px 10px 4px", background:p.surface }}>
        <span style={{ fontSize:8.5, fontWeight:700, color:p.textStrong }}>10:42</span>
        <span style={{ fontSize:7, color:p.textDim }}>● WiFi</span>
      </div>
      {/* header */}
      <div style={{ background:p.surface, padding:"0 10px 8px", display:"flex", alignItems:"center", justifyContent:"space-between" }}>
        <div style={{ display:"flex", alignItems:"center", gap:7 }}>
          <div style={{ width:28, height:28, borderRadius:"50%", background:"#1D3047", display:"flex", alignItems:"center", justifyContent:"center" }}>
            <span style={{ fontSize:9, fontWeight:700, color:"white" }}>AK</span>
          </div>
          <div>
            <p style={{ fontSize:7, color:p.textMuted, lineHeight:1.3 }}>Good morning,</p>
            <p style={{ fontSize:10.5, fontWeight:700, color:p.textStrong, lineHeight:1.3 }}>Ayesha</p>
          </div>
        </div>
        <div style={{ position:"relative" }}>
          <div style={{ width:26, height:26, borderRadius:"50%", background:p.surfaceAlt, display:"flex", alignItems:"center", justifyContent:"center" }}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={p.textMuted} strokeWidth="2.2" strokeLinecap="round"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></svg>
          </div>
          <div style={{ position:"absolute", top:-2, right:-2, width:12, height:12, borderRadius:"50%", background:"#EF4444", display:"flex", alignItems:"center", justifyContent:"center" }}>
            <span style={{ fontSize:4, color:"white", fontWeight:700 }}>9+</span>
          </div>
        </div>
      </div>
      {/* next ride */}
      <div style={{ padding:"8px 10px 0" }}>
        <div style={{ borderRadius:14, padding:"10px 12px", background:"linear-gradient(135deg,#1D3047,#2D5F9E)" }}>
          <p style={{ fontSize:6, color:"rgba(255,255,255,0.55)", textTransform:"uppercase", letterSpacing:"0.06em", marginBottom:3 }}>YOUR NEXT RIDE</p>
          <p style={{ fontSize:10.5, fontWeight:700, color:"white", lineHeight:1.3 }}>BUS-002 · Engineering Loop</p>
          <div style={{ display:"flex", alignItems:"center", gap:7, marginTop:5 }}>
            <span style={{ background:"#E3B02E", color:"#1D3047", fontSize:6.5, fontWeight:700, padding:"2px 6px", borderRadius:4 }}>8 MIN AWAY</span>
            <span style={{ fontSize:6.5, color:"rgba(255,255,255,0.6)" }}>Tech Park · Stop 03</span>
          </div>
        </div>
      </div>
      {/* quick access */}
      <div style={{ padding:"8px 10px 0", flex:1 }}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:6 }}>
          <p style={{ fontSize:9, fontWeight:700, color:p.textStrong }}>Quick Access</p>
          <p style={{ fontSize:7, color:"#2D5F9E", fontWeight:600 }}>See all</p>
        </div>
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:5 }}>
          {([
            { label:"Buses",   sub:"14 active", bg:"#1D3047", icon:<svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="3" width="20" height="14" rx="3"/><path d="M2 9h20"/><path d="M7 17v2"/><path d="M17 17v2"/></svg> },
            { label:"Routes",  sub:"22 mapped", bg:"#E3B02E", icon:<svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/></svg> },
            { label:"Drivers", sub:"36+",        bg:"#9C27B0", icon:<svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="7" r="4"/><path d="M5.5 20a7 7 0 0 1 13 0"/></svg> },
            { label:"Riders",  sub:"2,184",      bg:"#FF6B35", icon:<svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/></svg> },
          ] as { label:string; sub:string; bg:string; icon:React.ReactNode }[]).map(item => (
            <div key={item.label} style={{ background:p.surface, borderRadius:10, padding:"8px", display:"flex", alignItems:"center", gap:6, boxShadow:p.shadow, overflow:"hidden" }}>
              <div style={{ width:22, height:22, borderRadius:"50%", background:item.bg, display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>{item.icon}</div>
              <div style={{ minWidth:0 }}>
                <p style={{ fontSize:8.5, fontWeight:700, color:p.textStrong, lineHeight:1.2 }}>{item.label}</p>
                <p style={{ fontSize:6.5, color:p.textMuted, lineHeight:1.2 }}>{item.sub}</p>
              </div>
            </div>
          ))}
        </div>
        {/* face check-in */}
        <div style={{ marginTop:5, background:p.surface, borderRadius:10, padding:"7px 8px", display:"flex", alignItems:"center", gap:8, boxShadow:p.shadow }}>
          <div style={{ width:30, height:30, borderRadius:9, background:"#E91E63", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.2" strokeLinecap="round"><path d="M9 3H5a2 2 0 0 0-2 2v4m6-6h10a2 2 0 0 1 2 2v4M9 3v18m0 0h10a2 2 0 0 0 2-2v-4M9 21H5a2 2 0 0 1-2-2v-4m0 0h18"/></svg>
          </div>
          <div>
            <p style={{ fontSize:6.5, color:p.textMuted, textTransform:"uppercase", letterSpacing:"0.05em" }}>SECURE</p>
            <p style={{ fontSize:9, fontWeight:700, color:p.textStrong }}>Face Check-In</p>
          </div>
          <p style={{ fontSize:6.5, color:p.textMuted, marginLeft:"auto" }}>Tap to...</p>
        </div>
      </div>
      {/* bottom nav */}
      <div style={{ background:p.surface, borderTop:`1px solid ${p.divider}`, display:"flex", alignItems:"flex-end", justifyContent:"space-around", padding:"6px 4px 5px", minHeight:42 }}>
        {([
          { icon:<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#2D5F9E" strokeWidth="2.2" strokeLinecap="round"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/></svg>, label:"Home", active:true },
          { icon:<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={p.textDim} strokeWidth="2.2" strokeLinecap="round"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>, label:"Search" },
          { icon:<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.4" strokeLinecap="round"><rect x="2" y="3" width="20" height="14" rx="3"/><path d="M2 9h20"/><path d="M7 17v2"/><path d="M17 17v2"/></svg>, label:"Buses", center:true },
          { icon:<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={p.textDim} strokeWidth="2.2" strokeLinecap="round"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></svg>, label:"Alerts" },
          { icon:<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={p.textDim} strokeWidth="2.2" strokeLinecap="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>, label:"Chat" },
        ] as { icon:React.ReactNode; label:string; active?:boolean; center?:boolean }[]).map((item,i) => (
          <div key={i} style={{ display:"flex", flexDirection:"column", alignItems:"center", gap:2, ...(item.center ? { position:"relative", top:-10 } : {}) }}>
            <div style={item.center ? { width:34, height:34, borderRadius:"50%", background:"#1D3047", display:"flex", alignItems:"center", justifyContent:"center", boxShadow:"0 4px 12px rgba(29,48,71,0.35)" } : { display:"flex", alignItems:"center", justifyContent:"center" }}>
              {item.icon}
            </div>
            <span style={{ fontSize:5.5, color: item.active ? "#2D5F9E" : item.center ? p.textStrong : p.textDim, fontWeight: item.active || item.center ? 700 : 500 }}>{item.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ─── Map screen ─── */
function MapScreen({ p }: { p: Palette }) {
  return (
    <div style={{ width:"100%", height:"100%", display:"flex", flexDirection:"column", background:p.mapTerrain }}>
      {/* status bar */}
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"6px 12px 4px", background:p.surface }}>
        <span style={{ fontSize:8.5, fontWeight:700, color:p.textStrong }}>10:42</span>
        <span style={{ fontSize:7, color:p.textDim }}>WiFi ●●●</span>
      </div>
      {/* header */}
      <div style={{ background:p.surface, padding:"6px 10px 8px", display:"flex", alignItems:"center", gap:8, borderBottom:`1px solid ${p.divider}` }}>
        <div style={{ width:22, height:22, borderRadius:"50%", background:p.surfaceAlt, display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
          <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke={p.textMuted} strokeWidth="2.5" strokeLinecap="round"><path d="M15 18l-6-6 6-6"/></svg>
        </div>
        <div style={{ flex:1, minWidth:0 }}>
          <p style={{ fontSize:10, fontWeight:700, color:p.textStrong, lineHeight:1.2 }}>Bus 002 · Engineering Loop</p>
          <p style={{ fontSize:7, color:p.textMuted, lineHeight:1.2 }}>Tech Park → University Gate B</p>
        </div>
        <div style={{ display:"flex", alignItems:"center", gap:3, padding:"2px 6px", borderRadius:10, background:"#FFF1F1" }}>
          <div style={{ width:5, height:5, borderRadius:"50%", background:"#EF4444" }}/>
          <span style={{ fontSize:6.5, color:"#EF4444", fontWeight:700 }}>LIVE</span>
        </div>
      </div>
      {/* map */}
      <div style={{ position:"relative", flex:1, overflow:"hidden" }}>
        <svg style={{ position:"absolute", inset:0, width:"100%", height:"100%" }} viewBox="0 0 250 310" preserveAspectRatio="none">
          <rect width="250" height="310" fill={p.mapTerrain}/>
          <rect x="0" y="0" width="105" height="135" fill={p.mapBlock} rx="2"/>
          <rect x="145" y="0" width="105" height="135" fill={p.mapBlock2}/>
          <rect x="0" y="155" width="250" height="155" fill={p.mapBlock3}/>
          <rect x="105" y="0" width="40" height="310" fill={p.mapRoad}/>
          <rect x="119" y="0" width="4" height="310" fill={p.mapRoadAlt} opacity="0.5"/>
          <rect x="0" y="135" width="250" height="14" fill={p.mapRoad}/>
          <ellipse cx="55" cy="215" rx="42" ry="30" fill="#C8D8B8" opacity="0.65"/>
          <text x="12" y="72" fontSize="8" fill={p.mapText} fontFamily="Inter,sans-serif">TECH PARK</text>
          <text x="152" y="215" fontSize="7" fill={p.mapTextSub} fontFamily="Inter,sans-serif">CENTRAL AVE</text>
          <text x="14" y="270" fontSize="8" fill={p.mapTextSub} fontFamily="Inter,sans-serif">UNIVERSITY</text>
          <line x1="125" y1="22" x2="125" y2="295" stroke="#2D5F9E" strokeWidth="13" strokeOpacity="0.12" strokeLinecap="round"/>
          <line x1="125" y1="22" x2="125" y2="295" stroke="#2D5F9E" strokeWidth="4.5" strokeLinecap="round"/>
          <circle cx="125" cy="44" r="5" fill={p.surface} stroke="#2D5F9E" strokeWidth="2.5"/>
          <circle cx="125" cy="140" r="5" fill={p.surface} stroke="#2D5F9E" strokeWidth="2.5"/>
          <circle cx="125" cy="210" r="7" fill="#E3B02E" stroke={p.surface} strokeWidth="2.5"/>
          <circle cx="125" cy="278" r="5" fill={p.surface} stroke="#2D5F9E" strokeWidth="2.5"/>
        </svg>
        <div style={{ position:"absolute", top:36, left:"54%", marginLeft:5, background:p.surface, borderRadius:8, padding:"2px 6px", boxShadow:"0 2px 8px rgba(0,0,0,0.1)" }}>
          <p style={{ fontSize:7, fontWeight:700, color:p.textStrong }}>Tech Park</p>
        </div>
        <div style={{ position:"absolute", top:130, left:"54%", marginLeft:5, background:p.surface, borderRadius:8, padding:"2px 6px", boxShadow:"0 2px 8px rgba(0,0,0,0.1)" }}>
          <p style={{ fontSize:7, fontWeight:700, color:p.textStrong }}>Library</p>
          <p style={{ fontSize:5.5, color:p.textMuted }}>10:34 ·</p>
        </div>
        <div style={{ position:"absolute", top:198, left:"56%", marginLeft:8, background:"#E3B02E", borderRadius:10, padding:"4px 7px", boxShadow:"0 4px 12px rgba(227,176,46,0.4)" }}>
          <p style={{ fontSize:7.5, fontWeight:700, color:"#1D3047" }}>Now · Sci. Block</p>
          <p style={{ fontSize:6, color:"#1D3047", opacity:0.75 }}>arriving 2 min</p>
        </div>
        <div style={{ position:"absolute", top:266, left:"25%", transform:"translateX(-50%)", background:p.surface, borderRadius:8, padding:"2px 6px", boxShadow:"0 2px 8px rgba(0,0,0,0.1)" }}>
          <p style={{ fontSize:7, fontWeight:700, color:p.textStrong }}>Hostel C</p>
          <p style={{ fontSize:5.5, color:p.textMuted }}>10:46</p>
        </div>
      </div>
      {/* driver info */}
      <div style={{ background:p.surface, borderTop:`1px solid ${p.divider}`, padding:"8px 12px 10px" }}>
        <div style={{ display:"flex", alignItems:"center", gap:8 }}>
          <div style={{ width:28, height:28, borderRadius:"50%", background:p.surfaceAlt, display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
            <span style={{ fontSize:8, fontWeight:700, color:p.textMuted }}>RH</span>
          </div>
          <div style={{ flex:1, minWidth:0 }}>
            <p style={{ fontSize:9, fontWeight:700, color:p.textStrong, lineHeight:1.2 }}>Driver Rashid Hameed</p>
            <p style={{ fontSize:6.5, color:p.textMuted }}>Bus 002 · Plate JET-4421 · ⭐ 4.9</p>
          </div>
          <div style={{ width:24, height:24, borderRadius:"50%", background:"#E3B02E", display:"flex", alignItems:"center", justifyContent:"center" }}>
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#1D3047" strokeWidth="2.5" strokeLinecap="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.59 1h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 8.58a16 16 0 0 0 5.51 5.51l.95-.95a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/></svg>
          </div>
        </div>
        <div style={{ display:"flex", alignItems:"center", gap:7, marginTop:6 }}>
          <div style={{ flex:1, height:4, background:p.surfaceAlt, borderRadius:4, overflow:"hidden" }}>
            <div style={{ width:"62%", height:"100%", background:"#2D5F9E", borderRadius:4 }}/>
          </div>
          <span style={{ fontSize:7, color:p.textMuted }}>62% complete</span>
        </div>
      </div>
    </div>
  );
}

/* ─── Main section ─── */
export function AppShowcase() {
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === "dark";
  const p = PALETTES[isDark ? "dark" : "light"];

  const features = [
    "Real-time bus tracking with live ETAs",
    "AI-optimized routes for your schedule",
    "Smart card boarding & face check-in",
    "One-tap emergency & complaint flow",
  ];

  /* Floating cards anchor on the section bg — adapt with theme. */
  const cs: React.CSSProperties = {
    display:"flex", alignItems:"center", gap:10,
    background: isDark ? "#1b2230" : "white",
    border: isDark ? "1px solid #222a3a" : "none",
    borderRadius:16,
    padding:"10px 14px 10px 10px",
    boxShadow: isDark
      ? "0 10px 32px rgba(0,0,0,0.45), 0 1px 2px rgba(0,0,0,0.4)"
      : "0 8px 32px rgba(29,48,71,0.13), 0 1px 4px rgba(0,0,0,0.04)",
    position:"absolute", zIndex:30,
  };

  const ico = (bg: string, el: React.ReactNode) => (
    <div style={{ width:40, height:40, borderRadius:12, background:bg, display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>{el}</div>
  );

  const txt = (label: string, title: string) => (
    <div>
      <p style={{ fontSize:7, color: isDark ? "#8a93a6" : "#999", textTransform:"uppercase", letterSpacing:"0.07em", lineHeight:1.3, fontWeight:600 }}>{label}</p>
      <p style={{ fontSize:11.5, fontWeight:700, color: isDark ? "#eef0f5" : "#1D3047", lineHeight:1.3, whiteSpace:"nowrap" }}>{title}</p>
    </div>
  );

  return (
    <section id="download" className="py-14 sm:py-20 px-4 sm:px-8 lg:px-16 pb-24 sm:pb-28"
      style={{
        background: isDark
          ? "linear-gradient(135deg,#13161e 0%,#181c26 55%,#1b2230 100%)"
          : "linear-gradient(135deg,#F5F7FF 0%,#FEFEFE 55%,#FFFBF0 100%)",
        overflow:"visible",
      }}>
      <div className="max-w-7xl mx-auto flex flex-col lg:flex-row items-center gap-10 lg:gap-12">

        {/* ── LEFT ── */}
        <motion.div initial={{ opacity:0, x:-40 }} whileInView={{ opacity:1, x:0 }} viewport={{ once:true }} transition={{ duration:0.6 }}
          className="w-full lg:w-[40%] flex flex-col gap-5">

          <div style={{ display:"inline-flex", alignItems:"center", gap:6, padding:"5px 14px", borderRadius:50, background: isDark ? "#1b2230" : "#1D3047", border: isDark ? "1px solid #2a3346" : "none", width:"fit-content" }}>
            <div style={{ width:7, height:7, borderRadius:"50%", background:"#E3B02E" }}/>
            <span style={{ fontSize:11, fontWeight:700, color:"white", letterSpacing:"0.04em" }}>AVAILABLE NOW · V2.4</span>
          </div>

          <div>
            <h2 style={{ fontSize:52, fontWeight:900, lineHeight:1.05, color: isDark ? "#eef0f5" : "#1D3047", margin:0 }}>
              Download<br/><span style={{ color:"#E3B02E" }}>SaferSavvy</span>
            </h2>
            <p className="mt-3 text-sm text-gray-500 dark:text-ink-300 leading-relaxed max-w-md">
              The university transport companion that actually keeps up. <strong className="dark:text-ink-100">Track buses live, plan smarter routes, tap your smart card</strong>, and never miss a ride — all from one calm, focused app.
            </p>
          </div>

          <ul className="flex flex-col gap-2.5">
            {features.map(f => (
              <li key={f} className="flex items-center gap-3 text-sm text-gray-700 dark:text-ink-200">
                <span className="w-5 h-5 rounded-full bg-[#2D5F9E] flex items-center justify-center flex-shrink-0">
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                </span>
                {f}
              </li>
            ))}
          </ul>

          <div className="flex flex-wrap gap-3">
            <a href="https://drive.google.com/file/d/1-HnlZSwlj9SZsIMb-fpXGihX4XLv7Pq8/view?usp=sharing" target="_blank" rel="noopener noreferrer"
              className="flex items-center gap-3 px-5 py-3 rounded-xl text-white hover:-translate-y-0.5 transition-transform"
              style={{ background: isDark ? "#2D4A87" : "#1D3047", boxShadow: isDark ? "0 6px 20px rgba(0,0,0,0.5)" : "0 6px 20px rgba(29,48,71,0.3)" }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="white"><path d="M3.18 23.76c.33.18.7.22 1.06.12l11.39-6.57-2.38-2.38-10.07 8.83zM.35 1.28C.13 1.6 0 2.03 0 2.56v18.88c0 .53.13.96.35 1.28l.07.07 10.58-10.58v-.24L.42 1.21l-.07.07zM20.43 10.32l-2.91-1.68-2.65 2.65 2.65 2.65 2.93-1.69c.84-.48.84-1.27-.02-1.93zM4.24.12L15.63 6.69l-2.38 2.38L3.18.24C3.52.04 3.9.07 4.24.3v-.18z"/></svg>
              <div>
                <p className="text-[8px] opacity-60 uppercase tracking-widest">Download APK</p>
                <p className="text-[13px] font-bold leading-none">Android App</p>
              </div>
            </a>
            <a href="https://drive.google.com/file/d/1-HnlZSwlj9SZsIMb-fpXGihX4XLv7Pq8/view?usp=sharing" target="_blank" rel="noopener noreferrer"
              className="flex items-center gap-3 px-5 py-3 rounded-xl hover:-translate-y-0.5 transition-transform"
              style={{ background:"#E3B02E", color:"#1D3047", boxShadow:"0 6px 20px rgba(227,176,46,0.34)" }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#1D3047" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
              <div>
                <p className="text-[8px] opacity-60 uppercase tracking-widest">Direct Download</p>
                <p className="text-[13px] font-bold leading-none">Get APK</p>
              </div>
            </a>
          </div>

          <div className="flex flex-wrap items-center gap-x-5 gap-y-1 text-sm text-gray-400 dark:text-ink-400">
            <span>⭐⭐⭐⭐⭐ <strong className="text-gray-600 dark:text-ink-100">4.8</strong> · 12.4k reviews</span>
            <span>iOS 15+ · Android 9+</span>
            <span>Free · No ads</span>
          </div>
        </motion.div>

        {/* ── RIGHT: phones + floating cards ── */}
        <motion.div initial={{ opacity:0, x:40 }} whileInView={{ opacity:1, x:0 }} viewport={{ once:true }} transition={{ duration:0.7 }}
          className="w-full lg:w-[60%] relative" style={{ height:620 }}>

          {/* Card 1 — Live Bus Tracking (top, above left phone) */}
          <motion.div initial={{ opacity:0, y:-14 }} whileInView={{ opacity:1, y:0 }} viewport={{ once:true }} transition={{ duration:0.45, delay:0.3 }}
            style={{ ...cs, top:90, left:"16%" }}>
            {ico("#2D5F9E", <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>)}
            {txt("REAL-TIME", "Live Bus Tracking")}
          </motion.div>

          {/* Card 2 — Arriving in 2 min (right side, upper) */}
          <motion.div initial={{ opacity:0, x:14 }} whileInView={{ opacity:1, x:0 }} viewport={{ once:true }} transition={{ duration:0.45, delay:0.4 }}
            style={{ ...cs, top:155, right:"2%" }}>
            {ico("#00838F", <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>)}
            {txt("ARRIVING IN", "2 min")}
          </motion.div>

          {/* Card 3 — Smart Card Pay (right side, mid) */}
          <motion.div initial={{ opacity:0, x:14 }} whileInView={{ opacity:1, x:0 }} viewport={{ once:true }} transition={{ duration:0.45, delay:0.5 }}
            style={{ ...cs, top:268, right:"2%" }}>
            {ico("#E3B02E", <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#1D3047" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="5" width="20" height="14" rx="2"/><line x1="2" y1="10" x2="22" y2="10"/></svg>)}
            {txt("ONE-TAP", "Smart Card Pay")}
          </motion.div>

          {/* Card 4 — Smart Route Engine (right, lower) */}
          <motion.div initial={{ opacity:0, x:14 }} whileInView={{ opacity:1, x:0 }} viewport={{ once:true }} transition={{ duration:0.45, delay:0.6 }}
            style={{ ...cs, bottom:148, right:"2%" }}>
            {ico("#7B1FA2", <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3"/><path d="M12 1v4M12 19v4M4.22 4.22l2.83 2.83M16.95 16.95l2.83 2.83M1 12h4M19 12h4M4.22 19.78l2.83-2.83M16.95 7.05l2.83-2.83"/></svg>)}
            {txt("AI-POWERED", "Smart Route Engine")}
          </motion.div>

          {/* Card 5 — Emergency & Alerts (bottom center) */}
          <motion.div initial={{ opacity:0, y:14 }} whileInView={{ opacity:1, y:0 }} viewport={{ once:true }} transition={{ duration:0.45, delay:0.7 }}
            style={{ ...cs, bottom:-38, left:"42%" }}>
            {ico("#2E7D32", <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>)}
            {txt("SOS READY", "Emergency & Alerts")}
          </motion.div>

          {/* ── LEFT PHONE — admin (behind) ── */}
          <div className="absolute bottom-0 overflow-hidden"
            style={{
              left:"22%", width:200, height:420, borderRadius:36,
              background: isDark ? "#1b2230" : "#0A0F1A",
              border: isDark ? "1.5px solid #3a4358" : "none",
              transform:"rotate(-5deg)", zIndex:10,
              boxShadow: isDark
                ? "0 24px 60px rgba(0,0,0,0.6), 0 0 0 6px rgba(15,17,23,0.6), 0 0 0 7.5px rgba(58,67,88,0.5)"
                : "0 24px 60px rgba(0,0,0,0.38), 0 0 0 1.5px rgba(255,255,255,0.07)",
            }}>
            <div style={{ position:"absolute", left:-2.5, top:92, width:2.5, height:22, background: isDark ? "#3a4358" : "#1E2B3C", borderRadius:2 }}/>
            <div style={{ position:"absolute", left:-2.5, top:124, width:2.5, height:22, background: isDark ? "#3a4358" : "#1E2B3C", borderRadius:2 }}/>
            <div style={{ position:"absolute", right:-2.5, top:104, width:2.5, height:38, background: isDark ? "#3a4358" : "#1E2B3C", borderRadius:2 }}/>
            <div style={{ position:"absolute", inset:5, borderRadius:32, overflow:"hidden" }}><AdminScreen p={p} /></div>
            <div style={{ position:"absolute", bottom:6, left:"50%", transform:"translateX(-50%)", width:52, height:3, borderRadius:2, background:"rgba(255,255,255,0.25)", zIndex:50 }}/>
          </div>

          {/* ── RIGHT PHONE — map (front, larger) ── */}
          <div className="absolute bottom-0"
            style={{
              right:"8%", width:230, height:470, borderRadius:40,
              background: isDark ? "#1b2230" : "#0A0F1A",
              border: isDark ? "1.5px solid #3a4358" : "none",
              transform:"rotate(2deg)", zIndex:20,
              boxShadow: isDark
                ? "0 30px 70px rgba(0,0,0,0.7), 0 0 0 6px rgba(15,17,23,0.6), 0 0 0 7.5px rgba(63,94,174,0.45), 0 0 50px rgba(63,94,174,0.20)"
                : "0 30px 70px rgba(0,0,0,0.44), 0 0 0 1.5px rgba(255,255,255,0.09), 0 0 40px rgba(45,95,158,0.16)",
            }}>
            <div style={{ position:"absolute", left:-2.5, top:106, width:2.5, height:25, background: isDark ? "#3a4358" : "#1E2B3C", borderRadius:2 }}/>
            <div style={{ position:"absolute", left:-2.5, top:140, width:2.5, height:25, background: isDark ? "#3a4358" : "#1E2B3C", borderRadius:2 }}/>
            <div style={{ position:"absolute", right:-2.5, top:118, width:2.5, height:42, background: isDark ? "#3a4358" : "#1E2B3C", borderRadius:2 }}/>
            <div style={{ position:"absolute", inset:6, borderRadius:36, overflow:"hidden" }}><MapScreen p={p} /></div>
            <div style={{ position:"absolute", bottom:7, left:"50%", transform:"translateX(-50%)", width:62, height:3, borderRadius:2, background:"rgba(255,255,255,0.25)", zIndex:50 }}/>
          </div>

        </motion.div>
      </div>
    </section>
  );
}

export default AppShowcase;
